import type { CertificateEntry } from "../../../../../../../../../../shared/types/certificates.type";
import type {
	SaveCertificatesRequest,
	SocialCasinoSaveEntry,
} from "../../../../../../../../../../shared/types/save-certificates.type";
import {
	SOCIAL_CASINO_GROUP_1_ISO,
	SOCIAL_CASINO_GROUP_2_ISO,
	matchSocialCasinoGroups,
	type SocialCasinoGroupId,
} from "../../../../../../../../../../shared/constants/social-casino-groups";
import { PRIORITY_GAMBLING_GEOS } from "../../../../../../../../../../shared/constants/priority-gambling-geos";
import { normalizeAccountCardId } from "../../../../../../../../../../shared/utils/normalize-account-card-id";

export type SocialCasinoDraftEntry = {
	appBundleId: string;
	groupIds: SocialCasinoGroupId[];
};

export type CertificatesDraft = {
	gambling: string[];
	socialCasino: SocialCasinoDraftEntry[];
};

const isSocialCasino = (cert: CertificateEntry) =>
	(cert.cert_type || "").toLowerCase() === "social_casino";

const GROUP_ISOS: Record<SocialCasinoGroupId, readonly string[]> = {
	"1": SOCIAL_CASINO_GROUP_1_ISO,
	"2": SOCIAL_CASINO_GROUP_2_ISO,
};

/** Build editable draft from account-card certificates. */
export function buildCertificatesDraft(
	certs: CertificateEntry[],
): CertificatesDraft {
	const gambling = [
		...new Set(
			certs
				.filter((c) => !isSocialCasino(c))
				.map((c) => c.geo.trim().toUpperCase())
				.filter(Boolean),
		),
	].sort((a, b) => a.localeCompare(b));

	const byBundle = new Map<string, Set<string>>();
	for (const cert of certs.filter(isSocialCasino)) {
		const bundle = (cert.app_bundle_id || "").trim();
		if (!bundle) continue;
		const geos = byBundle.get(bundle) ?? new Set<string>();
		geos.add(cert.geo.trim().toUpperCase());
		byBundle.set(bundle, geos);
	}

	const socialCasino: SocialCasinoDraftEntry[] = [];
	for (const [appBundleId, geos] of byBundle) {
		const { groups } = matchSocialCasinoGroups(geos);
		// If GEOs do not sufficiently cover Group 1/2 (due to scrape/mapping gaps),
		// we keep the UI clean and do not create an editable empty row.
		if (!groups.length) continue;
		socialCasino.push({
			appBundleId,
			groupIds: groups.map((g) => g.id),
		});
	}

	socialCasino.sort((a, b) => a.appBundleId.localeCompare(b.appBundleId));

	return { gambling, socialCasino };
}

/** Inline gambling chips: priority list first, then any extra selected geos. */
export function gamblingChipGeos(
	selected: string[],
	priority: readonly string[] = PRIORITY_GAMBLING_GEOS,
): string[] {
	const priorityUpper = priority.map((g) => g.toUpperCase());
	const prioritySet = new Set(priorityUpper);
	const selectedSet = new Set(selected.map((g) => g.toUpperCase()));
	const extras = [...selectedSet]
		.filter((g) => !prioritySet.has(g))
		.sort((a, b) => a.localeCompare(b));
	return [...priorityUpper, ...extras];
}

export function toggleGeo(list: string[], geo: string): string[] {
	const code = geo.trim().toUpperCase();
	if (!/^[A-Z]{2}$/.test(code)) return list;
	const set = new Set(list.map((g) => g.toUpperCase()));
	if (set.has(code)) set.delete(code);
	else set.add(code);
	return [...set].sort((a, b) => a.localeCompare(b));
}

export function toggleSocialGroup(
	entry: SocialCasinoDraftEntry,
	groupId: SocialCasinoGroupId,
): SocialCasinoDraftEntry {
	const set = new Set(entry.groupIds);
	if (set.has(groupId)) set.delete(groupId);
	else set.add(groupId);
	return {
		...entry,
		groupIds: (["1", "2"] as SocialCasinoGroupId[]).filter((id) => set.has(id)),
	};
}

export function expandSocialGroups(
	groupIds: SocialCasinoGroupId[],
): string[] {
	const geos = new Set<string>();
	for (const id of groupIds) {
		for (const iso of GROUP_ISOS[id]) geos.add(iso);
	}
	return [...geos].sort((a, b) => a.localeCompare(b));
}

/** Desired-state payload for App Service (always sends both sections). */
export function buildSaveCertificatesPayload(
	accountId: string,
	draft: CertificatesDraft,
): SaveCertificatesRequest {
	const social_casino: SocialCasinoSaveEntry[] = draft.socialCasino
		.map((entry) => ({
			app_bundle_id: entry.appBundleId,
			geos: expandSocialGroups(entry.groupIds),
		}))
		.filter((entry) => entry.geos.length > 0);

	return {
		account_id: normalizeAccountCardId(accountId),
		gambling: [...draft.gambling]
			.map((g) => g.toUpperCase())
			.sort((a, b) => a.localeCompare(b)),
		social_casino,
	};
}

export function draftsEqual(a: CertificatesDraft, b: CertificatesDraft): boolean {
	return (
		JSON.stringify(buildSaveCertificatesPayload("0000000000", a)) ===
		JSON.stringify(buildSaveCertificatesPayload("0000000000", b))
	);
}
